import { api } from "../api/client";
import type {
  BugRecentRow,
  ErrorRecentRow,
  ErrorSummaryGroup,
  EventRecentRow,
  FeatureRecentRow,
  TopAppRow,
} from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { ErrorView, LoadingView } from "../components/StateView";
import { absoluteTime, formatNumber, relativeTime } from "../format";
import { useApi } from "../useApi";
import styles from "./HomePage.module.css";

export function HomePage() {
  const state = useApi(() => api.summary(), []);

  if (state.kind === "loading") return <Shell><LoadingView /></Shell>;
  if (state.kind === "error") {
    return (
      <Shell>
        <ErrorView message={state.message} onRetry={state.reload} />
      </Shell>
    );
  }

  const d = state.data;

  return (
    <Shell>
      <section className={styles.stats}>
        <StatCard
          label="Bug reports"
          value={d.bugReports.total}
          delta={d.bugReports.last7d}
          deltaLabel="last 7 days"
        />
        <StatCard
          label="Feature requests"
          value={d.featureRequests.total}
          delta={d.featureRequests.last7d}
          deltaLabel="last 7 days"
        />
        <StatCard
          label="Errors (7d)"
          value={d.errors.last7d}
          delta={d.errors.devicesLast7d}
          deltaLabel="unique devices"
        />
        <StatCard
          label="Sessions (7d)"
          value={d.analytics.sessionsLast7d}
          delta={d.analytics.devicesLast7d}
          deltaLabel="unique devices"
        />
      </section>

      <div className={styles.grid}>
        <TopErrors rows={d.errors.topGroups} />
        <RecentErrors rows={d.errors.recent} />
        <RecentBugs rows={d.bugReports.recent} />
        <RecentFeatures rows={d.featureRequests.recent} />
        <TopApps rows={d.analytics.topAppsLast7d} />
        <RecentEvents rows={d.analytics.recentEvents} />
      </div>
    </Shell>
  );
}

/* ============================================================== */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <PageHeader title="Overview" subtitle="At-a-glance pulse for the suite." />
      {children}
    </>
  );
}

function StatCard({
  label,
  value,
  delta,
  deltaLabel,
}: {
  label: string;
  value: number;
  delta: number;
  deltaLabel: string;
}) {
  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{formatNumber(value)}</div>
      <div className={styles.statDelta}>
        <strong>{formatNumber(delta)}</strong> {deltaLabel}
      </div>
    </div>
  );
}

function PanelHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <header className={styles.panelHeader}>
      <h2 className={styles.panelTitle}>{title}</h2>
      {hint ? <span className={styles.panelHint}>{hint}</span> : null}
    </header>
  );
}

function RecentBugs({ rows }: { rows: BugRecentRow[] }) {
  return (
    <section className={styles.panel}>
      <PanelHeader title="Recent bug reports" hint="last 5" />
      {rows.length === 0 ? (
        <div className={styles.empty}>None yet.</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.id} className={styles.listItem}>
              <span className={styles.listTitle}>{r.title}</span>
              <span className={styles.listMeta}>
                <span title={absoluteTime(r.created_at)}>
                  {relativeTime(r.created_at)}
                </span>
                {r.suite_version ? <span> · v{r.suite_version}</span> : null}
                {r.os ? <span> · {r.os}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentFeatures({ rows }: { rows: FeatureRecentRow[] }) {
  return (
    <section className={styles.panel}>
      <PanelHeader title="Recent feature requests" hint="last 5" />
      {rows.length === 0 ? (
        <div className={styles.empty}>None yet.</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.id} className={styles.listItem}>
              <span className={styles.listTitle}>{r.title}</span>
              <span className={styles.listMeta}>
                <span title={absoluteTime(r.created_at)}>
                  {relativeTime(r.created_at)}
                </span>
                {r.suite_version ? <span> · v{r.suite_version}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TopApps({ rows }: { rows: TopAppRow[] }) {
  const max = rows[0]?.n ?? 1;
  return (
    <section className={styles.panel}>
      <PanelHeader title="Top apps opened" hint="last 7 days" />
      {rows.length === 0 ? (
        <div className={styles.empty}>No app.opened events yet.</div>
      ) : (
        <ul className={styles.barList}>
          {rows.map((r) => (
            <li key={r.app_id} className={styles.barRow}>
              <span className={styles.barLabel}>{r.app_id}</span>
              <span className={styles.barTrack}>
                <span
                  className={styles.barFill}
                  style={{ width: `${(r.n / max) * 100}%` }}
                />
              </span>
              <span className={styles.barValue}>{r.n}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentEvents({ rows }: { rows: EventRecentRow[] }) {
  return (
    <section className={styles.panel}>
      <PanelHeader title="Recent events" hint="latest 8" />
      {rows.length === 0 ? (
        <div className={styles.empty}>No analytics events yet.</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.id} className={styles.listItem}>
              <span className={styles.listTitle}>
                <code className={styles.eventType}>{r.event_type}</code>
                {r.app_id ? (
                  <span className={styles.eventApp}>{r.app_id}</span>
                ) : null}
              </span>
              <span
                className={styles.listMeta}
                title={absoluteTime(r.created_at)}
              >
                {relativeTime(r.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TopErrors({ rows }: { rows: ErrorSummaryGroup[] }) {
  return (
    <section className={styles.panel}>
      <PanelHeader title="Top errors" hint="last 7d, by frequency" />
      {rows.length === 0 ? (
        <div className={styles.empty}>No errors recorded.</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.fingerprint} className={styles.listItem}>
              <span className={styles.listTitle}>
                <code className={styles.eventType}>{r.kind}</code>
                {r.app_id ? (
                  <span className={styles.eventApp}>{r.app_id}</span>
                ) : null}
                <span className={styles.eventApp}>
                  · {formatNumber(r.occurrences)}× · {r.devices}d
                </span>
              </span>
              <span
                className={styles.listMeta}
                title={absoluteTime(r.last_seen)}
              >
                {r.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RecentErrors({ rows }: { rows: ErrorRecentRow[] }) {
  return (
    <section className={styles.panel}>
      <PanelHeader title="Recent errors" hint="latest 8" />
      {rows.length === 0 ? (
        <div className={styles.empty}>No errors recorded.</div>
      ) : (
        <ul className={styles.list}>
          {rows.map((r) => (
            <li key={r.id} className={styles.listItem}>
              <span className={styles.listTitle}>
                <code className={styles.eventType}>{r.kind}</code>
                {r.app_id ? (
                  <span className={styles.eventApp}>{r.app_id}</span>
                ) : null}
              </span>
              <span
                className={styles.listMeta}
                title={absoluteTime(r.created_at)}
              >
                {r.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
